import React, { useState, useEffect } from 'react';
import { getEnvironmentFromDomain } from '../../utils/helperFunctions';

import config from '../../config';
const setBifrostUrl = (environment = 'qa', flow = 'VIDEO_STREAM') => {
  const BIFROST_URLS = {
    DEV_IMAGE_STREAM: 'https://bifrost-plana.ops.enncrypto.com',
    DEV_VIDEO_STREAM: 'https://d.api.enncrypto.com',
    QA_IMAGE_STREAM: 'https://q.api.enncrypto.com',
    QA_VIDEO_STREAM: 'https://q.api.enncrypto.com',
    STAGE_IMAGE_STREAM: 'https://s.api.enncrypto.com',
    STAGE_VIDEO_STREAM: 'https://s.api.enncrypto.com',
    PROD_IMAGE_STREAM: 'https://api.enncrypto.com',
    PROD_VIDEO_STREAM: 'https://api.enncrypto.com',
  }
  const key = `${environment.toUpperCase()}_${flow}`
  return BIFROST_URLS[key] || BIFROST_URLS.QA_VIDEO_STREAM
}

// Function to get bandwidth test endpoint based on environment and flow
const getBandwidthTestEndpoint = (flow = 'VIDEO_STREAM') => {
  const environment = getEnvironmentFromDomain();
  const baseUrl = setBifrostUrl(environment, flow);
  return `${config.API_URL}/estimate-bandwidth`;
};

// Bandwidth test configuration factory
const getBandwidthTestConfig = (flow = 'VIDEO_STREAM') => ({
  // Test different packet sizes to get accurate measurements
  packetSizes: [32, 64, 128, 256, 512, 1024], // in KB
  maxTestDuration: 10000, // 10 seconds max for all tests
  requestTimeout: 5000, // 5 seconds timeout per request
  minSamples: 3, // Minimum number of successful tests
  // Dynamic endpoint based on environment and flow
  testEndpoint: getBandwidthTestEndpoint(flow),
});

interface BandwidthTestProps {
  onComplete?: (results: any) => void;
  onError?: (error: Error) => void;
  flow?: string; // Optional flow parameter, defaults to VIDEO_STREAM
}

const BandwidthTest: React.FC<BandwidthTestProps> = ({ onComplete, onError, flow = 'VIDEO_STREAM' }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<any>(null);
  const [config, setConfig] = useState(() => getBandwidthTestConfig(flow));

  // Update config when flow changes
  useEffect(() => {
    setConfig(getBandwidthTestConfig(flow));
  }, [flow]);

  // Generate random data more efficiently
  const generateTestData = (sizeKB) => {
    const sizeBytes = sizeKB * 1024;
    // Create typed array directly without filling with random values
    // Random data doesn't significantly affect bandwidth measurement
    const buffer = new ArrayBuffer(sizeBytes);
    return new Blob([buffer], { type: 'application/octet-stream' });
  };

  // Perform a single upload test
  const performSingleTest = async (sizeKB) => {
    const blob = generateTestData(sizeKB);
    const startTime = performance.now();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.requestTimeout);
      
      const response = await fetch(config.testEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'Content-Length': blob.size.toString(),
        },
        body: blob,
        signal: controller.signal,
        // Disable request body compression for accurate measurement
        duplex: 'half',
      });
      
      clearTimeout(timeoutId);
      const endTime = performance.now();
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Calculate upload speed
      const durationSeconds = (endTime - startTime) / 1000;
      const sizeMegabits = (sizeKB * 1024 * 8) / (1024 * 1024);
      const speedMbps = sizeMegabits / durationSeconds;
      
      return {
        sizeKB,
        sizeBytes: sizeKB * 1024,
        durationMs: endTime - startTime,
        speedMbps,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log(`Test timeout for ${sizeKB}KB packet`);
        return null;
      }
      throw error;
    }
  };

  // Advanced bandwidth estimation using adaptive testing
  const performBandwidthTest = async () => {
    setIsRunning(true);
    setProgress(0);
    
    const testResults = [];
    const startTime = performance.now();
    
    try {
      // Test with progressively larger packets
      for (let i = 0; i < config.packetSizes.length; i++) {
        if (performance.now() - startTime > config.maxTestDuration) {
          console.log('Max test duration reached');
          break;
        }
        
        const sizeKB = config.packetSizes[i];
        const result = await performSingleTest(sizeKB);
        
        if (result) {
          testResults.push(result);
          setProgress(((i + 1) / config.packetSizes.length) * 100);
          
          // If this packet took too long (>80% of timeout), skip larger sizes
          if (result.durationMs > config.requestTimeout * 0.8) {
            console.log(`Stopping at ${sizeKB}KB due to slow connection`);
            break;
          }
        }
      }
      
      // Calculate statistics
      if (testResults.length === 0) {
        throw new Error('No successful bandwidth tests completed');
      }
      
      // Find the maximum speed (best case, usually from larger packets)
      const maxSpeed = Math.max(...testResults.map(r => r.speedMbps));
      const bestResult = testResults.find(r => r.speedMbps === maxSpeed);
      
      // Also calculate percentiles for additional insights
      const speeds = testResults.map(r => r.speedMbps).sort((a, b) => a - b);
      const p90Index = Math.floor(speeds.length * 0.9);
      const p95Index = Math.floor(speeds.length * 0.95);
      
      // Calculate final statistics
      const finalResults = {
        // Maximum upload speed (Mbps) - the best case
        speedMbps: maxSpeed,
        maxSpeedMbps: maxSpeed,
        // Additional statistics for reference
        p90SpeedMbps: speeds[Math.min(p90Index, speeds.length - 1)],
        p95SpeedMbps: speeds[Math.min(p95Index, speeds.length - 1)],
        minSpeedMbps: Math.min(...speeds),
        // Best test details
        bestPacketSizeKB: bestResult?.sizeKB,
        bestTestDurationMs: bestResult?.durationMs,
        // Total data uploaded
        totalBytesUploaded: testResults.reduce((sum, r) => sum + r.sizeBytes, 0),
        // Test metadata
        totalDurationMs: performance.now() - startTime,
        testsCompleted: testResults.length,
        largestPacketTestedKB: testResults[testResults.length - 1]?.sizeKB,
        // Connection quality assessment based on max speed
        connectionQuality: assessConnectionQuality(maxSpeed),
        // Environment and endpoint info
        environment: getEnvironmentFromDomain(),
        flow: flow,
        endpoint: config.testEndpoint,
        // Raw results for debugging
        detailedResults: testResults,
        timestamp: new Date().toISOString(),
      };
      
      setResults(finalResults);
      setProgress(100);
      
      // Call the completion callback
      if (onComplete) {
        onComplete(finalResults);
      }
      
      return finalResults;
      
    } catch (error) {
      console.error('Bandwidth test error:', error);
      const errorResult = {
        error: error.message,
        speedMbps: 1, // Fallback to 1 Mbps
        maxSpeedMbps: 1,
        connectionQuality: 'unknown',
        testsCompleted: testResults.length,
        environment: getEnvironmentFromDomain(),
        flow: flow,
        endpoint: config.testEndpoint,
        timestamp: new Date().toISOString(),
      };
      
      setResults(errorResult);
      
      if (onError) {
        onError(error);
      }
      
      return errorResult;
    } finally {
      setIsRunning(false);
    }
  };

  // Assess connection quality based on max speed
  const assessConnectionQuality = (speedMbps) => {
    if (speedMbps < 1) return 'poor';
    if (speedMbps < 5) return 'fair';
    if (speedMbps < 25) return 'good';
    if (speedMbps >= 25) return 'excellent';
    return 'unknown';
  };

  // Format bytes to human readable
  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Auto-run on component mount if needed
  useEffect(() => {
    // Uncomment to auto-run on mount
    // performBandwidthTest();
  }, []);

  return (
    <div className="p-4 bg-gray-900 text-white rounded-lg max-w-md mx-auto">
      <h3 className="text-xl font-bold mb-4">Bandwidth Test</h3>
      
      {!isRunning && !results && (
        <button
          onClick={performBandwidthTest}
          className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
        >
          Start Bandwidth Test
        </button>
      )}
      
      {isRunning && (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">Testing upload speed...</p>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-400">{Math.round(progress)}% complete</p>
        </div>
      )}
      
      {results && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-800 p-3 rounded">
              <p className="text-xs text-gray-400">Upload Speed</p>
              <p className="text-lg font-bold">{results.speedMbps?.toFixed(2) || '?'} Mbps</p>
            </div>
            <div className="bg-gray-800 p-3 rounded">
              <p className="text-xs text-gray-400">Connection Quality</p>
              <p className="text-lg font-bold capitalize">{results.connectionQuality || 'Unknown'}</p>
            </div>
          </div>
          
          {results.error && (
            <div className="bg-red-900/50 p-3 rounded">
              <p className="text-sm text-red-300">Error: {results.error}</p>
            </div>
          )}
          
          <details className="cursor-pointer">
            <summary className="text-sm text-gray-400 hover:text-gray-300">
              View detailed results
            </summary>
            <div className="mt-2 space-y-2 text-xs">
              <p>Environment: {results.environment}</p>
              <p>Flow: {results.flow}</p>
              <p>Endpoint: {results.endpoint}</p>
              <p>Tests completed: {results.testsCompleted}</p>
              <p>Best packet size: {results.bestPacketSizeKB}KB</p>
              <p>Total uploaded: {formatBytes(results.totalBytesUploaded || 0)}</p>
              <p>Test duration: {((results.totalDurationMs || 0) / 1000).toFixed(2)}s</p>
              {results.p90SpeedMbps && (
                <p>90th percentile: {results.p90SpeedMbps.toFixed(2)} Mbps</p>
              )}
              {results.minSpeedMbps && (
                <p>Range: {results.minSpeedMbps.toFixed(2)} - {results.maxSpeedMbps.toFixed(2)} Mbps</p>
              )}
            </div>
          </details>
          
          <button
            onClick={performBandwidthTest}
            className="w-full py-2 px-4 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors text-sm"
          >
            Test Again
          </button>
        </div>
      )}
    </div>
  );
};

// Export the bandwidth test function for use in your existing component
export const performBandwidthTest = async (flow = 'VIDEO_STREAM') => {
  // This is the standalone version that can be imported and used
  const config = getBandwidthTestConfig(flow);
  const testResults = [];
  const startTime = performance.now();
  
  const generateTestData = (sizeKB) => {
    const sizeBytes = sizeKB * 1024;
    const buffer = new ArrayBuffer(sizeBytes);
    return new Blob([buffer], { type: 'application/octet-stream' });
  };
  
  const performSingleTest = async (sizeKB) => {
    const blob = generateTestData(sizeKB);
    const startTime = performance.now();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.requestTimeout);
      
      const response = await fetch(config.testEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'Content-Length': blob.size.toString(),
        },
        body: blob,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const endTime = performance.now();
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const durationSeconds = (endTime - startTime) / 1000;
      const sizeMegabits = (sizeKB * 1024 * 8) / (1024 * 1024);
      const speedMbps = sizeMegabits / durationSeconds;
      
      return {
        sizeKB,
        sizeBytes: sizeKB * 1024,
        durationMs: endTime - startTime,
        speedMbps,
      };
    } catch (error) {
      console.error(`Test failed for ${sizeKB}KB:`, error.message);
      return null;
    }
  };
  
  try {
    // Perform tests with increasing packet sizes
    for (const sizeKB of config.packetSizes) {
      if (performance.now() - startTime > config.maxTestDuration) {
        break;
      }
      
      const result = await performSingleTest(sizeKB);
      if (result) {
        testResults.push(result);
        
        // If the test is taking too long, stop testing larger sizes
        if (result.durationMs > config.requestTimeout * 0.8) {
          console.log(`Stopping at ${sizeKB}KB due to slow connection`);
          break;
        }
      }
    }
    
    if (testResults.length === 0) {
      throw new Error('No successful bandwidth tests completed');
    }
    
    // Use the maximum speed as the best estimate
    const maxSpeedMbps = Math.max(...testResults.map(r => r.speedMbps));
    const bestResult = testResults.find(r => r.speedMbps === maxSpeedMbps);
    const totalBytes = testResults.reduce((sum, r) => sum + r.sizeBytes, 0);
    
    return {
      speedMbps: maxSpeedMbps, // Best case speed
      avgSpeedMbps: maxSpeedMbps, // For backward compatibility
      maxSpeedMbps: maxSpeedMbps,
      bytesReceived: totalBytes,
      durationMs: performance.now() - startTime,
      testsCompleted: testResults.length,
      largestPacketKB: testResults[testResults.length - 1]?.sizeKB || 0,
      bestPacketKB: bestResult?.sizeKB,
      environment: getEnvironmentFromDomain(),
      flow: flow,
      endpoint: config.testEndpoint,
      detailedResults: testResults,
    };
    
  } catch (error) {
    console.error('Bandwidth test error:', error);
    return {
      bytesReceived: 0,
      durationMs: 0,
      speedMbps: 1, // Default to 1 Mbps on failure
      avgSpeedMbps: 1,
      maxSpeedMbps: 1,
      testsCompleted: 0,
      environment: getEnvironmentFromDomain(),
      flow: flow,
      endpoint: config.testEndpoint,
      error: error.message,
    };
  }
};

export default BandwidthTest;